/**
 * ログイン状態（distinctId）の遷移から、PostHog に対して行うべき
 * identify / reset 操作を決定する純関数。
 *
 * identify / reset の分岐（ログイン / ログアウト / ユーザー切替）は本来
 * {@link PostHogProvider} の useEffect 内にあるが、React のレンダリングなしで
 * 単体テストできるよう、判定ロジックだけをここに切り出している。
 */

import type { PersonProperties } from './types';

/** distinctId 遷移に対して PostHog へ行う操作。 */
type IdentityAction =
  | { type: 'identify'; distinctId: string; personProperties?: PersonProperties }
  | { type: 'resetThenIdentify'; distinctId: string; personProperties?: PersonProperties }
  | { type: 'reset' }
  | { type: 'none' };

/**
 * 直前と最新の distinctId から、実行すべき identify / reset 操作を求める。
 *
 * @param previousDistinctId 直前にセットされていた distinctId（未ログインは `null`）
 * @param nextDistinctId 最新の distinctId（ログアウト中は `null` / `undefined`）
 * @param personProperties identify 時に `$set` するデモグラ属性。ログイン時のみ載せる。
 */
export const resolveIdentityTransition = (
  previousDistinctId: string | null,
  nextDistinctId: string | null | undefined,
  personProperties?: PersonProperties,
): IdentityAction => {
  const hasLoggedIn = Boolean(nextDistinctId) && nextDistinctId !== previousDistinctId;
  const hasLoggedOut = !nextDistinctId && Boolean(previousDistinctId);

  if (hasLoggedIn && nextDistinctId) {
    // personProperties が無いときはキー自体を載せない（action に undefined を残さない）。
    const withPersonProperties = personProperties ? { personProperties } : {};
    // ログアウトを挟まないユーザー切替（別 distinctId へ直接遷移）では、
    // 前ユーザーの匿名状態が新ユーザーに混ざらないよう先に reset してから identify する。
    return previousDistinctId
      ? { type: 'resetThenIdentify', distinctId: nextDistinctId, ...withPersonProperties }
      : { type: 'identify', distinctId: nextDistinctId, ...withPersonProperties };
  }
  if (hasLoggedOut) {
    // 共有PCで次ユーザーが前 distinctId を継承しないよう reset する。
    return { type: 'reset' };
  }
  return { type: 'none' };
};
